import React, { useRef, useState } from 'react'
import Input, { InputProps } from './input'
import { useClickOutside } from './util'

export type AutocompleteItemProps = {
  id: string | number
} & object

type AutocompleteProps<T extends AutocompleteItemProps> = {
  items: T[]
  // eslint-disable-next-line no-unused-vars
  onItemSelected?: (item: T) => void
  // eslint-disable-next-line no-unused-vars
  itemComponent: React.FC<T>
} & InputProps

const Autocomplete = <T extends AutocompleteItemProps>({
  items,
  onItemSelected = undefined,
  itemComponent: ItemComponent,
  onClear,
  ...input
}: AutocompleteProps<T>) => {
  const [isDisplay, setIsDisplay] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  useClickOutside(wrapperRef, () => {
    setIsDisplay(false)
  })

  const handleItemSelected = (val: T) => {
    if (onItemSelected) onItemSelected(val)
    setIsDisplay(false)
  }

  const autoCompletePanelRef = useRef<HTMLDivElement>(null)
  const autoCompleteInputRef = useRef<HTMLDivElement>(null)

  return (
    <div className="relative flex flex-col w-full" ref={wrapperRef}>
      <Input
        ref={autoCompleteInputRef}
        {...input}
        onClear={() => {
          setIsDisplay(false)
          if (onClear) onClear()
        }}
        onFocus={() => setIsDisplay(true)}
        onKeyDown={(e) => {
          if (['ArrowDown', 'Tab'].includes(e.key) && items.length > 0) {
            e.preventDefault()
            ;(autoCompletePanelRef?.current?.firstChild as HTMLElement)?.focus()
          }
        }}
      />
      {isDisplay && items.length > 0 && (
        <div
          ref={autoCompletePanelRef}
          role="menu"
          className="absolute flex flex-col items-stretch w-full overflow-hidden bg-white rounded-xl top-11"
          style={{
            zIndex: 21,
          }}
        >
          {items.map((val, index) => (
            <div
              key={val.id}
              className="px-4 py-1 text-left cursor-pointer hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
              onClick={() => {
                handleItemSelected(val)
              }}
              onKeyDown={(e) => {
                if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
                  e.preventDefault()
                  if (['ArrowDown'].includes(e.key)) {
                    ;(
                      autoCompletePanelRef.current?.childNodes[
                        index + 1
                      ] as HTMLElement
                    )?.focus()
                  }
                  if (['ArrowUp'].includes(e.key)) {
                    if (index === 0) {
                      autoCompleteInputRef.current?.focus()
                    }
                    ;(
                      autoCompletePanelRef.current?.childNodes[
                        index - 1
                      ] as HTMLElement
                    )?.focus()
                  }
                } else if (['Enter'].includes(e.key)) {
                  handleItemSelected(val)
                } else if (!['Tab', 'Shift'].includes(e.key)) {
                  autoCompleteInputRef.current?.focus()
                }
              }}
              role="menuitem"
              tabIndex={0}
            >
              <ItemComponent {...val} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Autocomplete
